import React, { useEffect, useState } from 'react'
import {Check, X, Trash2, Search, EyeOff, RefreshCw} from 'lucide-react'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import AdminLayout from '../../components/admin/AdminLayout'
import toast from 'react-hot-toast'

const TestimonialsManagement: React.FC = () => {
  const { data: testimonials, loading, fetchData, updateRecord, deleteRecord } = useEntityCRUD('testimonials')
  const { logAction } = useAdminLog()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('PENDING')

  // Carrega ao montar e toda vez que o filtro muda
  useEffect(() => {
    const filter: any = {}
    if (statusFilter !== 'ALL') {
      // Suporte a dados legados: PENDING inclui também 'pendente' (minúsculas)
      if (statusFilter === 'PENDING') {
        filter.status = { $in: ['PENDING', 'pendente'] }
      } else if (statusFilter === 'APPROVED') {
        filter.status = { $in: ['APPROVED', 'aprovado'] }
      } else if (statusFilter === 'HIDDEN') {
        filter.status = { $in: ['HIDDEN', 'oculto'] }
      } else if (statusFilter === 'REJECTED') {
        filter.status = { $in: ['REJECTED', 'rejeitado'] }
      } else {
        filter.status = statusFilter
      }
    }
    fetchData({ filter, sort: { createdAt: -1 } })
  }, [statusFilter])

  const handleApprove = async (id: string) => {
    try {
      await updateRecord(id, { status: 'APPROVED', updatedAt: new Date().toISOString() })
      await logAction('APPROVE', 'testimonials', id, 'Testimonial approved')
      toast.success('Testemunho aprovado!')
    } catch (error) {
      console.error('Failed to approve:', error)
      toast.error('Erro ao aprovar testemunho')
    }
  }

  const handleHide = async (id: string) => {
    try {
      await updateRecord(id, { status: 'HIDDEN', updatedAt: new Date().toISOString() })
      await logAction('HIDE', 'testimonials', id, 'Testimonial hidden')
      toast.success('Testemunho ocultado!')
    } catch (error) {
      console.error('Failed to hide:', error)
      toast.error('Erro ao ocultar testemunho')
    }
  }

  const handleReject = async (id: string) => {
    try {
      await updateRecord(id, { status: 'REJECTED', updatedAt: new Date().toISOString() })
      await logAction('REJECT', 'testimonials', id, 'Testimonial rejected')
      toast.success('Testemunho rejeitado!')
    } catch (error) {
      console.error('Failed to reject:', error)
      toast.error('Erro ao rejeitar testemunho')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este testemunho?')) return

    try {
      await deleteRecord(id)
      await logAction('DELETE', 'testimonials', id, 'Testimonial deleted')
      toast.success('Testemunho excluído!')
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error('Erro ao excluir testemunho')
    }
  }

  // Normaliza status legados para exibição
  const normalizeStatus = (status: string): string => {
    const map: Record<string, string> = {
      pendente: 'PENDING',
      aprovado: 'APPROVED',
      oculto: 'HIDDEN',
      rejeitado: 'REJECTED',
    }
    return map[status?.toLowerCase()] || status
  }

  const filteredTestimonials = testimonials.filter(
    (t) =>
      t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.message?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      PENDING: 'Pendente',
      APPROVED: 'Aprovado',
      HIDDEN: 'Oculto',
      REJECTED: 'Rejeitado',
      DELETED: 'Excluído',
    }
    return labels[normalizeStatus(status)] || status
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      HIDDEN: 'bg-gray-100 text-gray-800',
      REJECTED: 'bg-red-100 text-red-800',
      DELETED: 'bg-red-200 text-red-900',
    }
    return styles[normalizeStatus(status)] || 'bg-gray-100 text-gray-800'
  }

  // Decide quais botões mostrar baseado no status normalizado
  const normStatus = (t: any) => normalizeStatus(t.status)

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Testemunhos</h1>
          <p className="text-gray-600">Modere e gerencie os testemunhos da comunidade — REFUGE IN CHRIST MINISTRY</p>
        </div>
        <button
          onClick={() => {
            const filter: any = {}
            if (statusFilter !== 'ALL') {
              if (statusFilter === 'PENDING') filter.status = { $in: ['PENDING', 'pendente'] }
              else if (statusFilter === 'APPROVED') filter.status = { $in: ['APPROVED', 'aprovado'] }
              else if (statusFilter === 'HIDDEN') filter.status = { $in: ['HIDDEN', 'oculto'] }
              else if (statusFilter === 'REJECTED') filter.status = { $in: ['REJECTED', 'rejeitado'] }
              else filter.status = statusFilter
            }
            fetchData({ filter, sort: { createdAt: -1 } })
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#152d6b] transition-colors text-sm"
        >
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou mensagem..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
          >
            <option value="ALL">Todos os Status</option>
            <option value="PENDING">Pendentes</option>
            <option value="APPROVED">Aprovados</option>
            <option value="HIDDEN">Ocultos</option>
            <option value="REJECTED">Rejeitados</option>
          </select>
        </div>

        {/* Contador */}
        {!loading && (
          <p className="text-sm text-gray-500 mt-3">
            {filteredTestimonials.length} testemunho{filteredTestimonials.length !== 1 ? 's' : ''} encontrado{filteredTestimonials.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTestimonials.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              <p className="text-lg font-medium mb-2">Nenhum testemunho encontrado</p>
              <p className="text-sm">Tente mudar o filtro de status ou o termo de busca</p>
            </div>
          ) : (
            filteredTestimonials.map((testimonial: any) => (
              <div key={testimonial._id} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#1e3a8a]/20 hover:border-[#1e3a8a]/60 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">{testimonial.name || 'Sem nome'}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(testimonial.status)}`}>
                        {getStatusLabel(testimonial.status)}
                      </span>
                      {testimonial.email && (
                        <span className="text-xs text-gray-400">{testimonial.email}</span>
                      )}
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{testimonial.message}</p>
                    <p className="text-xs text-gray-400 mt-3">
                      Enviado em: {testimonial.createdAt ? new Date(testimonial.createdAt).toLocaleString('pt-BR') : '—'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-100 flex-wrap">
                  {/* Aprovar: aparece para qualquer status que não seja APPROVED */}
                  {normStatus(testimonial) !== 'APPROVED' && (
                    <button
                      onClick={() => handleApprove(testimonial._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                    >
                      <Check size={16} /> Aprovar
                    </button>
                  )}

                  {/* Ocultar: apenas para aprovados */}
                  {normStatus(testimonial) === 'APPROVED' && (
                    <button
                      onClick={() => handleHide(testimonial._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                      <EyeOff size={16} /> Ocultar
                    </button>
                  )}

                  {/* Rejeitar: para pendentes e ocultos */}
                  {(normStatus(testimonial) === 'PENDING' || normStatus(testimonial) === 'HIDDEN') && (
                    <button
                      onClick={() => handleReject(testimonial._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                    >
                      <X size={16} /> Rejeitar
                    </button>
                  )}

                  {/* Excluir: sempre disponível */}
                  <button
                    onClick={() => handleDelete(testimonial._id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium ml-auto"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </AdminLayout>
  )
}

export default TestimonialsManagement
